import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, createTextVNode as _createTextVNode, unref as _unref, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, mergeProps as _mergeProps, Fragment as _Fragment, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "white--text font-weight-bold" }
const _hoisted_2 = { class: "mr-2" }
const _hoisted_3 = { class: "drawer-header" }
const _hoisted_4 = { class: "user-info" }
const _hoisted_5 = { class: "white--text text-h6 font-weight-bold" }
const _hoisted_6 = { class: "user-name" }
const _hoisted_7 = { class: "user-role" }

import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { collection, getDocs, onSnapshot, query, where } from 'firebase/firestore'
import { db } from '@/services/firebase'
import { useAuthStore } from '@/store/modules/auth'
import { notificationService, UserNotification } from '@/services/notificationService'


export default /*@__PURE__*/_defineComponent({
  __name: 'AppHeader',
  setup(__props) {

const router = useRouter()
const route = useRoute()
const authStore = useAuthStore()
const drawer = ref(false)

// Rol bazlı görsel ipuçları (avatar rengi, ikon, etiket). Patron (boss) için
// admin/öğrenciden ayrı bir kimlik gösterilir.
// Boss isAdmin'e dahil olduğundan her birinde önce boss'u kontrol et.
const roleAvatarColor = computed(() => {
  if (authStore.isBoss) return 'deep-purple'
  if (authStore.isAdmin) return 'amber'
  return 'success'
})
const roleIcon = computed(() => {
  if (authStore.isBoss) return 'mdi-crown'
  if (authStore.isAdmin) return 'mdi-account-cog'
  return 'mdi-account'
})
const roleLabel = computed(() => {
  if (authStore.isBoss) return 'Patron'
  if (authStore.isAdmin) return 'Yönetici'
  return 'Öğrenci'
})

// Notifications Badge Logic
const notifCount = ref(0)
const pendingStudentsCount = ref(0)

// Firestore'daki admin bildirimlerinde approval_pending olanlar zaten var;
// users koleksiyonundan gelenler ise Firestore'da karşılığı olmayan pending öğrenciler.
// Çift saymamak için: Firestore'daki approval_pending bildirimlerinin relatedData (userId) setini tut,
// users'dan gelenleri sadece o sete girmeyenleri say.
const approvalPendingUserIds = ref<Set<string>>(new Set())

const pendingCount = computed(() => {
  const extraPending = pendingStudentsCount.value - approvalPendingUserIds.value.size
  return notifCount.value + Math.max(0, extraPending)
})

let unsubscribeNotifications: (() => void) | null = null
let unsubscribeUsers: (() => void) | null = null

const setupNotificationsListener = () => {
  if (unsubscribeNotifications) {
    unsubscribeNotifications()
    unsubscribeNotifications = null
  }
  if (unsubscribeUsers) {
    unsubscribeUsers()
    unsubscribeUsers = null
  }

  if (authStore.user) {
    unsubscribeNotifications = notificationService.subscribeToNotifications(
      authStore.user.id,
      authStore.user.role,
      (notifications: UserNotification[]) => {
        notifCount.value = notifications.length
        approvalPendingUserIds.value = new Set(
          notifications
            .filter(n => n.type === 'approval_pending' && n.relatedData)
            .map(n => typeof n.relatedData === 'string' ? n.relatedData : n.relatedData?.userId)
            .filter(Boolean)
        )
      }
    )

    // Boss admin-eşidir: bekleyen kayıt rozetini o da görür.
    if (authStore.isAdmin) {
      // Okuma optimizasyonu: tüm users koleksiyonunu dinlemek yerine yalnızca
      // bekleyen öğrencileri sorgula. Böylece herhangi bir user yazımında değil,
      // sadece bekleyen-öğrenci dokümanları değiştiğinde yeniden okuma olur.
      // `deleted` filtresi BELLEKTE yapılır: eski dokümanlarda alan olmayabilir,
      // `where('deleted','==',false)` bunları yanlışlıkla eler ve ayrıca
      // role+status+deleted üçlü filtresi composite index gerektirirdi.
      const pendingStudentsQuery = query(
        collection(db, 'users'),
        where('role', '==', 'student'),
        where('status', '==', 'pending')
      )
      unsubscribeUsers = onSnapshot(pendingStudentsQuery, (snapshot) => {
        let pending = 0
        snapshot.forEach((doc) => {
          const d = doc.data()
          if (!d.deleted) pending++
        })
        pendingStudentsCount.value = pending
      })
    }
  }
}

watch(() => authStore.user, (user) => {
  if (user) {
    setupNotificationsListener()
  } else {
    notifCount.value = 0
    pendingStudentsCount.value = 0
    approvalPendingUserIds.value = new Set()
    if (unsubscribeNotifications) {
      unsubscribeNotifications()
      unsubscribeNotifications = null
    }
    if (unsubscribeUsers) {
      unsubscribeUsers()
      unsubscribeUsers = null
    }
  }
}, { immediate: true })

onUnmounted(() => {
  if (unsubscribeNotifications) {
    unsubscribeNotifications()
  }
  if (unsubscribeUsers) {
    unsubscribeUsers()
  }
})

const logout = async () => {
  await authStore.logout()
  router.push('/')
  drawer.value = false
}

return (_ctx: any,_cache: any) => {
  const _component_router_link = _resolveComponent("router-link")!
  const _component_v_spacer = _resolveComponent("v-spacer")!
  const _component_v_icon = _resolveComponent("v-icon")!
  const _component_v_btn = _resolveComponent("v-btn")!
  const _component_v_badge = _resolveComponent("v-badge")!
  const _component_v_avatar = _resolveComponent("v-avatar")!
  const _component_v_list_item_title = _resolveComponent("v-list-item-title")!
  const _component_v_list_item = _resolveComponent("v-list-item")!
  const _component_v_divider = _resolveComponent("v-divider")!
  const _component_v_list = _resolveComponent("v-list")!
  const _component_v_menu = _resolveComponent("v-menu")!
  const _component_v_toolbar_items = _resolveComponent("v-toolbar-items")!
  const _component_v_container = _resolveComponent("v-container")!
  const _component_v_app_bar = _resolveComponent("v-app-bar")!
  const _component_v_chip = _resolveComponent("v-chip")!
  const _component_v_navigation_drawer = _resolveComponent("v-navigation-drawer")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(_component_v_app_bar, {
      app: "",
      elevation: "0",
      height: "80",
      class: "modern-header"
    }, {
      default: _withCtx(() => [
        _cache[29] || (_cache[29] = _createElementVNode("div", { class: "header-background" }, null, -1)),
        _createVNode(_component_v_container, {
          class: "d-flex align-center",
          style: {"height":"100%","position":"relative","z-index":"2"}
        }, {
          default: _withCtx(() => [
            _createVNode(_component_router_link, {
              to: { name: 'Home' },
              class: "brand-link d-flex align-center mr-8"
            }, {
              default: _withCtx(() => _cache[15] || (_cache[15] = [
                _createElementVNode("div", { class: "brand-logo" }, [
                  _createElementVNode("img", {
                    src: "/Uta-logo.svg",
                    alt: "Uta Logo",
                    height: "36"
                  })
                ], -1),
                _createElementVNode("div", { class: "brand-text ml-3 hidden-sm-and-down" }, [
                  _createElementVNode("h2", { class: "brand-title" }, "Urla Tenis Akademi")
                ], -1)
              ])),
              _: 1,
              __: [15]
            }),
            _createVNode(_component_v_spacer),
            _createVNode(_component_v_toolbar_items, { class: "hidden-sm-and-down" }, {
              default: _withCtx(() => [
                _createVNode(_component_v_btn, {
                  variant: "text",
                  color: "white",
                  to: { name: 'Home' },
                  class: "nav-btn mx-1"
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_v_icon, {
                      start: "",
                      icon: "mdi-home"
                    }),
                    _cache[16] || (_cache[16] = _createTextVNode(" Ana Sayfa "))
                  ]),
                  _: 1,
                  __: [16]
                }),
                (_unref(authStore).isAuthenticated)
                  ? (_openBlock(), _createBlock(_component_v_btn, {
                      key: 0,
                      variant: "text",
                      color: "white",
                      to: { name: 'Courts' },
                      class: "nav-btn mx-1"
                    }, {
                      default: _withCtx(() => [
                        _createVNode(_component_v_icon, {
                          start: "",
                          icon: "mdi-tennis-ball"
                        }),
                        _cache[17] || (_cache[17] = _createTextVNode(" Kortlar "))
                      ]),
                      _: 1,
                      __: [17]
                    }))
                  : _createCommentVNode("", true),
                (_unref(authStore).isAdmin || _unref(authStore).isBoss)
                  ? (_openBlock(), _createBlock(_component_v_btn, {
                      key: 1,
                      variant: "text",
                      color: "white",
                      to: { name: 'Pricing' },
                      class: "nav-btn mx-1"
                    }, {
                      default: _withCtx(() => [
                        _createVNode(_component_v_icon, {
                          start: "",
                          icon: "mdi-currency-usd"
                        }),
                        _cache[18] || (_cache[18] = _createTextVNode(" Fiyatlar "))
                      ]),
                      _: 1,
                      __: [18]
                    }))
                  : _createCommentVNode("", true),
                _createVNode(_component_v_btn, {
                  variant: "text",
                  color: "white",
                  to: { name: 'Contact' },
                  class: "nav-btn mx-1"
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_v_icon, {
                      start: "",
                      icon: "mdi-phone"
                    }),
                    _cache[19] || (_cache[19] = _createTextVNode(" İletişim "))
                  ]),
                  _: 1,
                  __: [19]
                }),
                (_unref(authStore).isAuthenticated && (_unref(authStore).isAdmin || _unref(authStore).isBoss || _unref(authStore).isStudent))
                  ? (_openBlock(), _createBlock(_component_v_btn, {
                      key: 2,
                      variant: "text",
                      color: "white",
                      to: { name: _unref(authStore).isStudent ? 'StudentNotifications' : 'Notifications' },
                      class: "nav-btn mx-1"
                    }, {
                      default: _withCtx(() => [
                        _createVNode(_component_v_badge, {
                          content: pendingCount.value,
                          color: "error",
                          "model-value": pendingCount.value > 0,
                          "offset-x": "-2",
                          "offset-y": "-2"
                        }, {
                          default: _withCtx(() => [
                            _createVNode(_component_v_icon, {
                              class: "mr-1",
                              icon: "mdi-bell-outline"
                            })
                          ]),
                          _: 1
                        }, 8, ["content", "model-value"]),
                        _cache[20] || (_cache[20] = _createTextVNode(" Bildirimler "))
                      ]),
                      _: 1,
                      __: [20]
                    }, 8, ["to"]))
                  : _createCommentVNode("", true),
                (_unref(authStore).isAuthenticated)
                  ? (_openBlock(), _createBlock(_component_v_menu, {
                      key: 3,
                      "offset-y": "",
                      transition: "slide-y-transition"
                    }, {
                      activator: _withCtx(({ props }) => [
                        _createVNode(_component_v_btn, _mergeProps(props, {
                          variant: "text",
                          color: "white",
                          class: "user-menu-btn ml-3"
                        }), {
                          default: _withCtx(() => [
                            _createVNode(_component_v_avatar, {
                              size: "32",
                              color: roleAvatarColor.value,
                              class: "mr-2"
                            }, {
                              default: _withCtx(() => [
                                _createElementVNode("span", _hoisted_1, _toDisplayString(_unref(authStore).user?.firstName?.charAt(0)), 1)
                              ]),
                              _: 1
                            }, 8, ["color"]),
                            _createElementVNode("span", _hoisted_2, _toDisplayString(_unref(authStore).user?.firstName), 1),
                            _createVNode(_component_v_icon, {
                              icon: roleIcon.value,
                              size: "20",
                              class: "mr-1"
                            }, null, 8, ["icon"]),
                            _createVNode(_component_v_icon, {
                              icon: "mdi-chevron-down",
                              size: "16"
                            })
                          ]),
                          _: 2
                        }, 1040)
                      ]),
                      default: _withCtx(() => [
                        _createVNode(_component_v_list, {
                          class: "user-dropdown",
                          elevation: "8"
                        }, {
                          default: _withCtx(() => [
                            _createVNode(_component_v_list_item, {
                              to: { name: 'Profile' },
                              class: "dropdown-item"
                            }, {
                              default: _withCtx(() => [
                                _createVNode(_component_v_list_item_title, null, {
                                  default: _withCtx(() => [
                                    _createVNode(_component_v_icon, {
                                      icon: "mdi-account",
                                      class: "mr-2"
                                    }),
                                    _cache[21] || (_cache[21] = _createTextVNode(" Profilim "))
                                  ]),
                                  _: 1,
                                  __: [21]
                                })
                              ]),
                              _: 1
                            }),
                            (_unref(authStore).isStudent)
                              ? (_openBlock(), _createBlock(_component_v_list_item, {
                                  key: 0,
                                  to: { name: 'StudentDashboard' },
                                  class: "dropdown-item"
                                }, {
                                  default: _withCtx(() => [
                                    _createVNode(_component_v_list_item_title, null, {
                                      default: _withCtx(() => [
                                        _createVNode(_component_v_icon, {
                                          icon: "mdi-view-dashboard",
                                          class: "mr-2"
                                        }),
                                        _cache[22] || (_cache[22] = _createTextVNode(" Panel "))
                                      ]),
                                      _: 1,
                                      __: [22]
                                    })
                                  ]),
                                  _: 1
                                }))
                              : _createCommentVNode("", true),
                            (_unref(authStore).isBoss)
                              ? (_openBlock(), _createBlock(_component_v_list_item, {
                                  key: 1,
                                  to: { name: 'BossDashboard' },
                                  class: "dropdown-item"
                                }, {
                                  default: _withCtx(() => [
                                    _createVNode(_component_v_list_item_title, null, {
                                      default: _withCtx(() => [
                                        _createVNode(_component_v_icon, {
                                          icon: "mdi-chart-line",
                                          class: "mr-2"
                                        }),
                                        _cache[23] || (_cache[23] = _createTextVNode(" İzleme Paneli "))
                                      ]),
                                      _: 1,
                                      __: [23]
                                    })
                                  ]),
                                  _: 1
                                }))
                              : _createCommentVNode("", true),
                            (_unref(authStore).isAdmin || _unref(authStore).isBoss)
                              ? (_openBlock(), _createBlock(_component_v_list_item, {
                                  key: 2,
                                  to: { name: 'AdminDashboard' },
                                  class: "dropdown-item"
                                }, {
                                  default: _withCtx(() => [
                                    _createVNode(_component_v_list_item_title, null, {
                                      default: _withCtx(() => [
                                        _createVNode(_component_v_icon, {
                                          icon: "mdi-shield-crown",
                                          class: "mr-2"
                                        }),
                                        _cache[24] || (_cache[24] = _createTextVNode(" Admin Panel "))
                                      ]),
                                      _: 1,
                                      __: [24]
                                    })
                                  ]),
                                  _: 1
                                }))
                              : _createCommentVNode("", true),
                            (_unref(authStore).isStudent)
                              ? (_openBlock(), _createBlock(_component_v_list_item, {
                                  key: 3,
                                  to: { name: 'Reservations' },
                                  class: "dropdown-item"
                                }, {
                                  default: _withCtx(() => [
                                    _createVNode(_component_v_list_item_title, null, {
                                      default: _withCtx(() => [
                                        _createVNode(_component_v_icon, {
                                          icon: "mdi-calendar-clock",
                                          class: "mr-2"
                                        }),
                                        _cache[25] || (_cache[25] = _createTextVNode(" Rezervasyonlar "))
                                      ]),
                                      _: 1,
                                      __: [25]
                                    })
                                  ]),
                                  _: 1
                                }))
                              : _createCommentVNode("", true),
                            (_unref(authStore).isAdmin || _unref(authStore).isBoss)
                              ? (_openBlock(), _createBlock(_component_v_list_item, {
                                  key: 4,
                                  to: { name: 'Attendance' },
                                  class: "dropdown-item"
                                }, {
                                  default: _withCtx(() => [
                                    _createVNode(_component_v_list_item_title, null, {
                                      default: _withCtx(() => [
                                        _createVNode(_component_v_icon, {
                                          icon: "mdi-clipboard-check",
                                          class: "mr-2"
                                        }),
                                        _cache[26] || (_cache[26] = _createTextVNode(" Yoklama "))
                                      ]),
                                      _: 1,
                                      __: [26]
                                    })
                                  ]),
                                  _: 1
                                }))
                              : _createCommentVNode("", true),
                            _createVNode(_component_v_divider, { class: "my-2" }),
                            _createVNode(_component_v_list_item, {
                              onClick: logout,
                              class: "dropdown-item logout-item"
                            }, {
                              default: _withCtx(() => [
                                _createVNode(_component_v_list_item_title, null, {
                                  default: _withCtx(() => [
                                    _createVNode(_component_v_icon, {
                                      icon: "mdi-logout",
                                      class: "mr-2"
                                    }),
                                    _cache[27] || (_cache[27] = _createTextVNode(" Çıkış Yap "))
                                  ]),
                                  _: 1,
                                  __: [27]
                                })
                              ]),
                              _: 1
                            })
                          ]),
                          _: 1
                        })
                      ]),
                      _: 1
                    }))
                  : (_openBlock(), _createBlock(_component_v_btn, {
                      key: 4,
                      variant: "outlined",
                      color: "white",
                      to: { name: 'Login' },
                      class: "auth-btn mr-2"
                    }, {
                      default: _withCtx(() => [
                        _createVNode(_component_v_icon, {
                          start: "",
                          icon: "mdi-login"
                        }),
                        _cache[28] || (_cache[28] = _createTextVNode(" Giriş Yap "))
                      ]),
                      _: 1,
                      __: [28]
                    }))
              ]),
              _: 1
            }),
            _createVNode(_component_v_btn, {
              icon: "",
              color: "white",
              class: "hidden-md-and-up ml-3",
              onClick: _cache[0] || (_cache[0] = ($event: any) => (drawer.value = !drawer.value))
            }, {
              default: _withCtx(() => [
                _createVNode(_component_v_icon, {
                  icon: "mdi-menu",
                  size: "28"
                })
              ]),
              _: 1
            })
          ]),
          _: 1
        })
      ]),
      _: 1,
      __: [29]
    }),
    _createVNode(_component_v_navigation_drawer, {
      modelValue: drawer.value,
      "onUpdate:modelValue": _cache[14] || (_cache[14] = ($event: any) => ((drawer).value = $event)),
      temporary: "",
      location: "right",
      width: "320",
      class: "mobile-drawer"
    }, {
      default: _withCtx(() => [
        _createElementVNode("div", _hoisted_3, [
          _cache[30] || (_cache[30] = _createElementVNode("div", { class: "drawer-brand" }, [
            _createElementVNode("img", {
              src: "/Uta-logo.svg",
              alt: "Uta Logo",
              height: "32"
            }),
            _createElementVNode("h3", { class: "drawer-title" }, "Tenis Akademi")
          ], -1)),
          _createVNode(_component_v_btn, {
            icon: "",
            color: "white",
            size: "small",
            onClick: _cache[1] || (_cache[1] = ($event: any) => (drawer.value = false))
          }, {
            default: _withCtx(() => [
              _createVNode(_component_v_icon, { icon: "mdi-close" })
            ]),
            _: 1
          })
        ]),
        _createVNode(_component_v_list, { class: "drawer-list" }, {
          default: _withCtx(() => [
            _createVNode(_component_v_list_item, {
              to: { name: 'Home' },
              class: "drawer-item",
              onClick: _cache[2] || (_cache[2] = ($event: any) => (drawer.value = false))
            }, {
              prepend: _withCtx(() => [
                _createVNode(_component_v_icon, { icon: "mdi-home" })
              ]),
              default: _withCtx(() => [
                _createVNode(_component_v_list_item_title, null, {
                  default: _withCtx(() => _cache[31] || (_cache[31] = [
                    _createTextVNode("Ana Sayfa")
                  ])),
                  _: 1,
                  __: [31]
                })
              ]),
              _: 1
            }),
            (_unref(authStore).isAuthenticated)
              ? (_openBlock(), _createBlock(_component_v_list_item, {
                  key: 0,
                  to: { name: 'Courts' },
                  class: "drawer-item",
                  onClick: _cache[3] || (_cache[3] = ($event: any) => (drawer.value = false))
                }, {
                  prepend: _withCtx(() => [
                    _createVNode(_component_v_icon, { icon: "mdi-tennis-ball" })
                  ]),
                  default: _withCtx(() => [
                    _createVNode(_component_v_list_item_title, null, {
                      default: _withCtx(() => _cache[32] || (_cache[32] = [
                        _createTextVNode("Kortlar")
                      ])),
                      _: 1,
                      __: [32]
                    })
                  ]),
                  _: 1
                }))
              : _createCommentVNode("", true),
            (_unref(authStore).isAdmin || _unref(authStore).isBoss)
              ? (_openBlock(), _createBlock(_component_v_list_item, {
                  key: 1,
                  to: { name: 'Pricing' },
                  class: "drawer-item",
                  onClick: _cache[4] || (_cache[4] = ($event: any) => (drawer.value = false))
                }, {
                  prepend: _withCtx(() => [
                    _createVNode(_component_v_icon, { icon: "mdi-currency-usd" })
                  ]),
                  default: _withCtx(() => [
                    _createVNode(_component_v_list_item_title, null, {
                      default: _withCtx(() => _cache[33] || (_cache[33] = [
                        _createTextVNode("Fiyatlar")
                      ])),
                      _: 1,
                      __: [33]
                    })
                  ]),
                  _: 1
                }))
              : _createCommentVNode("", true),
            _createVNode(_component_v_list_item, {
              to: { name: 'Contact' },
              class: "drawer-item",
              onClick: _cache[5] || (_cache[5] = ($event: any) => (drawer.value = false))
            }, {
              prepend: _withCtx(() => [
                _createVNode(_component_v_icon, { icon: "mdi-phone" })
              ]),
              default: _withCtx(() => [
                _createVNode(_component_v_list_item_title, null, {
                  default: _withCtx(() => _cache[34] || (_cache[34] = [
                    _createTextVNode("İletişim")
                  ])),
                  _: 1,
                  __: [34]
                })
              ]),
              _: 1
            }),
            (_unref(authStore).isAuthenticated && (_unref(authStore).isAdmin || _unref(authStore).isBoss || _unref(authStore).isStudent))
              ? (_openBlock(), _createBlock(_component_v_list_item, {
                  key: 2,
                  to: { name: _unref(authStore).isStudent ? 'StudentNotifications' : 'Notifications' },
                  class: "drawer-item",
                  onClick: _cache[6] || (_cache[6] = ($event: any) => (drawer.value = false))
                }, {
                  prepend: _withCtx(() => [
                    _createVNode(_component_v_badge, {
                      content: pendingCount.value,
                      color: "error",
                      "model-value": pendingCount.value > 0,
                      "offset-x": "-2",
                      "offset-y": "-2"
                    }, {
                      default: _withCtx(() => [
                        _createVNode(_component_v_icon, { icon: "mdi-bell-outline" })
                      ]),
                      _: 1
                    }, 8, ["content", "model-value"])
                  ]),
                  default: _withCtx(() => [
                    _createVNode(_component_v_list_item_title, null, {
                      default: _withCtx(() => [
                        _cache[35] || (_cache[35] = _createTextVNode(" Bildirimler ")),
                        (pendingCount.value > 0)
                          ? (_openBlock(), _createBlock(_component_v_chip, {
                              key: 0,
                              color: "error",
                              size: "x-small",
                              class: "ml-2"
                            }, {
                              default: _withCtx(() => [
                                _createTextVNode(_toDisplayString(pendingCount.value), 1)
                              ]),
                              _: 1
                            }))
                          : _createCommentVNode("", true)
                      ]),
                      _: 1,
                      __: [35]
                    })
                  ]),
                  _: 1
                }, 8, ["to"]))
              : _createCommentVNode("", true),
            (_unref(authStore).isAuthenticated)
              ? (_openBlock(), _createBlock(_component_v_divider, {
                  key: 3,
                  class: "my-4"
                }))
              : _createCommentVNode("", true),
            (_unref(authStore).isAuthenticated)
              ? (_openBlock(), _createElementBlock(_Fragment, { key: 4 }, [
                  _createElementVNode("div", _hoisted_4, [
                    _createVNode(_component_v_avatar, {
                      size: "48",
                      color: roleAvatarColor.value,
                      class: "mb-3"
                    }, {
                      default: _withCtx(() => [
                        _createElementVNode("span", _hoisted_5, _toDisplayString(_unref(authStore).user?.firstName?.charAt(0)), 1)
                      ]),
                      _: 1
                    }, 8, ["color"]),
                    _createElementVNode("h4", _hoisted_6, _toDisplayString(_unref(authStore).user?.firstName) + " " + _toDisplayString(_unref(authStore).user?.lastName), 1),
                    _createElementVNode("p", _hoisted_7, _toDisplayString(roleLabel.value), 1)
                  ]),
                  _createVNode(_component_v_list_item, {
                    to: { name: 'Profile' },
                    class: "drawer-item",
                    onClick: _cache[7] || (_cache[7] = ($event: any) => (drawer.value = false))
                  }, {
                    prepend: _withCtx(() => [
                      _createVNode(_component_v_icon, { icon: "mdi-account" })
                    ]),
                    default: _withCtx(() => [
                      _createVNode(_component_v_list_item_title, null, {
                        default: _withCtx(() => _cache[36] || (_cache[36] = [
                          _createTextVNode("Profilim")
                        ])),
                        _: 1,
                        __: [36]
                      })
                    ]),
                    _: 1
                  }),
                  (_unref(authStore).isStudent)
                    ? (_openBlock(), _createBlock(_component_v_list_item, {
                        key: 0,
                        to: { name: 'StudentDashboard' },
                        class: "drawer-item",
                        onClick: _cache[8] || (_cache[8] = ($event: any) => (drawer.value = false))
                      }, {
                        prepend: _withCtx(() => [
                          _createVNode(_component_v_icon, { icon: "mdi-view-dashboard" })
                        ]),
                        default: _withCtx(() => [
                          _createVNode(_component_v_list_item_title, null, {
                            default: _withCtx(() => _cache[37] || (_cache[37] = [
                              _createTextVNode("Panel")
                            ])),
                            _: 1,
                            __: [37]
                          })
                        ]),
                        _: 1
                      }))
                    : _createCommentVNode("", true),
                  (_unref(authStore).isBoss)
                    ? (_openBlock(), _createBlock(_component_v_list_item, {
                        key: 1,
                        to: { name: 'BossDashboard' },
                        class: "drawer-item",
                        onClick: _cache[9] || (_cache[9] = ($event: any) => (drawer.value = false))
                      }, {
                        prepend: _withCtx(() => [
                          _createVNode(_component_v_icon, { icon: "mdi-chart-line" })
                        ]),
                        default: _withCtx(() => [
                          _createVNode(_component_v_list_item_title, null, {
                            default: _withCtx(() => _cache[38] || (_cache[38] = [
                              _createTextVNode("İzleme Paneli")
                            ])),
                            _: 1,
                            __: [38]
                          })
                        ]),
                        _: 1
                      }))
                    : _createCommentVNode("", true),
                  (_unref(authStore).isAdmin || _unref(authStore).isBoss)
                    ? (_openBlock(), _createBlock(_component_v_list_item, {
                        key: 2,
                        to: { name: 'AdminDashboard' },
                        class: "drawer-item",
                        onClick: _cache[10] || (_cache[10] = ($event: any) => (drawer.value = false))
                      }, {
                        prepend: _withCtx(() => [
                          _createVNode(_component_v_icon, { icon: "mdi-shield-crown" })
                        ]),
                        default: _withCtx(() => [
                          _createVNode(_component_v_list_item_title, null, {
                            default: _withCtx(() => _cache[39] || (_cache[39] = [
                              _createTextVNode("Admin Panel")
                            ])),
                            _: 1,
                            __: [39]
                          })
                        ]),
                        _: 1
                      }))
                    : _createCommentVNode("", true),
                  (_unref(authStore).isStudent)
                    ? (_openBlock(), _createBlock(_component_v_list_item, {
                        key: 3,
                        to: { name: 'Reservations' },
                        class: "drawer-item",
                        onClick: _cache[11] || (_cache[11] = ($event: any) => (drawer.value = false))
                      }, {
                        prepend: _withCtx(() => [
                          _createVNode(_component_v_icon, { icon: "mdi-calendar-clock" })
                        ]),
                        default: _withCtx(() => [
                          _createVNode(_component_v_list_item_title, null, {
                            default: _withCtx(() => _cache[40] || (_cache[40] = [
                              _createTextVNode("Rezervasyonlar")
                            ])),
                            _: 1,
                            __: [40]
                          })
                        ]),
                        _: 1
                      }))
                    : _createCommentVNode("", true),
                  (_unref(authStore).isAdmin || _unref(authStore).isBoss)
                    ? (_openBlock(), _createBlock(_component_v_list_item, {
                        key: 4,
                        to: { name: 'Attendance' },
                        class: "drawer-item",
                        onClick: _cache[12] || (_cache[12] = ($event: any) => (drawer.value = false))
                      }, {
                        prepend: _withCtx(() => [
                          _createVNode(_component_v_icon, { icon: "mdi-clipboard-check" })
                        ]),
                        default: _withCtx(() => [
                          _createVNode(_component_v_list_item_title, null, {
                            default: _withCtx(() => _cache[41] || (_cache[41] = [
                              _createTextVNode("Yoklama")
                            ])),
                            _: 1,
                            __: [41]
                          })
                        ]),
                        _: 1
                      }))
                    : _createCommentVNode("", true),
                  _createVNode(_component_v_divider, { class: "my-4" }),
                  _createVNode(_component_v_list_item, {
                    onClick: logout,
                    class: "drawer-item logout-item"
                  }, {
                    prepend: _withCtx(() => [
                      _createVNode(_component_v_icon, { icon: "mdi-logout" })
                    ]),
                    default: _withCtx(() => [
                      _createVNode(_component_v_list_item_title, null, {
                        default: _withCtx(() => _cache[42] || (_cache[42] = [
                          _createTextVNode("Çıkış Yap")
                        ])),
                        _: 1,
                        __: [42]
                      })
                    ]),
                    _: 1
                  })
                ], 64))
              : (_openBlock(), _createElementBlock(_Fragment, { key: 5 }, [
                  _createVNode(_component_v_divider, { class: "my-4" }),
                  _createVNode(_component_v_list_item, {
                    to: { name: 'Login' },
                    class: "drawer-item",
                    onClick: _cache[13] || (_cache[13] = ($event: any) => (drawer.value = false))
                  }, {
                    prepend: _withCtx(() => [
                      _createVNode(_component_v_icon, { icon: "mdi-login" })
                    ]),
                    default: _withCtx(() => [
                      _createVNode(_component_v_list_item_title, null, {
                        default: _withCtx(() => _cache[43] || (_cache[43] = [
                          _createTextVNode("Giriş Yap")
                        ])),
                        _: 1,
                        __: [43]
                      })
                    ]),
                    _: 1
                  })
                ], 64))
          ]),
          _: 1
        })
      ]),
      _: 1
    }, 8, ["modelValue"])
  ], 64))
}
}

})